import React from "react";

// Memoized single row for CustomTable.
//
// WHY THIS EXISTS:
// CustomTable used to render every row inline inside its own render
// (tableBody.map -> div -> tableHeads.map -> cell renderer). So whenever the
// `body` array got a new reference - which happens legitimately on EVERY
// Firebase progress tick when even ONE campaign is running - ALL rows and ALL
// cells re-rendered, not just the row whose data changed. Profiler showed the
// table body accumulating ~12s of render time over a single session, which is
// what made the Create Campaign modal (a main-thread sibling) feel stuck.
//
// This component lets React skip rows whose data + relevant shared state did
// not change. Correctness rests on two facts:
//   1. The upstream merge (CampaignScreen.mergeFirebaseIntoRecord / EFFECT 2)
//      preserves the SAME row object reference when nothing the row displays
//      changed, and allocates a fresh object only on a real change. So a plain
//      `prev.row === next.row` compare is both cheap AND exhaustive - it covers
//      every column, every field, _firebaseProgress, state, pin, etc. A
//      consumer that does NOT preserve identity simply never benefits (its rows
//      always re-render, exactly like today) - it never goes stale.
//   2. `renderCell` is identity-stable (ref-backed in the parent) yet always
//      reads the latest closures when it actually runs. So the cell renderers
//      keep reading fresh checkedValue / isCopied / clickedRowData / handlers
//      at render time - no stale-closure trap - while their stable identity
//      means memo is never falsely invalidated.
//
// The remaining props are per-row primitives that exist ONLY to TRIGGER a
// re-render of the affected row(s) when cross-cutting interactive state changes
// (checkbox membership, the "select all across pages" strip, the copy
// highlight, and the last-clicked action row). The renderers themselves still
// read those values from the fresh closure - these props are just the signal
// that tells areEqual "this row needs to re-run".

const TableRow = ({ row, index, tableHeads, rowStyle, renderCell }) => {
  return (
    <div style={rowStyle}>
      {tableHeads.map((head, i) => (
        <React.Fragment key={i}>
          {renderCell(row, head, i, index)}
        </React.Fragment>
      ))}
    </div>
  );
};

function areEqual(prev, next) {
  // Structural props (stable by construction in the parent).
  if (prev.renderCell !== next.renderCell) return false; // ref-backed -> always equal
  if (prev.tableHeads !== next.tableHeads) return false; // identity-stable column list
  if (prev.rowStyle !== next.rowStyle) return false; // module constant
  if (prev.index !== next.index) return false; // re-render if this row moved position

  // Per-row interactive state surfaced as primitives so a change reaches ONLY
  // the affected row(s):
  if (prev.isRowChecked !== next.isRowChecked) return false; // this row's checkbox membership flipped
  if (prev.stripActive !== next.stripActive) return false; // "select all across pages" banner toggled
  if (prev.copiedCellKey !== next.copiedCellKey) return false; // copy highlight moved onto/off this row
  // The <3 action branch (and tableOperations generally) is derived from the
  // last-clicked row; bump on any click so action cells stay correct, exactly
  // matching today's behavior. Firebase ticks never touch this, so rows still
  // skip on a progress update - which is the whole point.
  if (prev.actionEpoch !== next.actionEpoch) return false;

  // Row data: a new reference is allocated only when something this row
  // displays actually changed (see WHY above). Reference compare is correct.
  return prev.row === next.row;
}

export default React.memo(TableRow, areEqual);
